import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Brain, ChevronDown, RefreshCw, Zap, AlertCircle, CheckCheck } from 'lucide-react';
import api from '../../services/api';

const ENTITY_MAP = { usa: 'Eyvo USA', india: 'Eyvo India' };

const fmtAmt = (n) => {
  const abs = Math.abs(n);
  const s = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(abs);
  return n < 0 ? <span className="text-red-600">-{s}</span> : <span className="text-emerald-700">{s}</span>;
};

const ConfidenceDot = ({ v }) => {
  if (!v) return null;
  const color = v >= 0.85 ? 'bg-emerald-400' : v >= 0.6 ? 'bg-amber-400' : 'bg-red-400';
  return <span title={`AI confidence ${Math.round(v * 100)}%`} className={`inline-block w-2 h-2 rounded-full ${color} ml-1`} />;
};

export default function PendingTransactions() {
  const { pathname } = useLocation();
  const entitySlug = pathname.split('/')[2];
  const entity = ENTITY_MAP[entitySlug] || entitySlug;

  const [txns, setTxns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('all');
  const [cascadeMsg, setCascadeMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The operational review queue. Membership is decided by
      // categorization state, not by how the transaction arrived, so a
      // Plaid-projected row and a CSV one appear here on the same terms.
      const [txRes, accRes] = await Promise.all([
        api.get(`/api/categorization/review-queue?entity=${encodeURIComponent(entity)}`),
        api.get(`/api/accounts/chart?entity=${encodeURIComponent(entity)}`),
      ]);
      setTxns(txRes.data);
      setAccounts(accRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const setBusy_ = (id, v) => setBusy(p => ({ ...p, [id]: v }));

  const categorize = async (txn_id, account_id, account_name) => {
    setBusy_(txn_id, 'cat');
    try {
      const account = accounts.find(a => a.account_id === account_id);
      const res = await api.put(`/api/categorization/transactions/${txn_id}/category`, {
        account_code: account?.account_code || account_id,
      });
      const also = res.data?.also_updated || 0;
      // Remove the transaction we just categorized plus any siblings the server auto-posted
      if (also > 0) {
        // Reload the full list so siblings disappear from the UI
        await load();
        setCascadeMsg(`Saved — also auto-categorized ${also} other ${also === 1 ? 'transaction' : 'transactions'} from the same vendor.`);
        setTimeout(() => setCascadeMsg(''), 6000);
      } else {
        setTxns(p => p.filter(t => t.txn_id !== txn_id));
      }
    } catch (e) { console.error(e); }
    setBusy_(txn_id, null);
  };

  const approve = async (txn_id) => {
    setBusy_(txn_id, 'approve');
    try {
      await api.post(`/api/categorization/transactions/${txn_id}/approve`);
      setTxns(p => p.filter(t => t.txn_id !== txn_id));
    } catch (e) { console.error(e); }
    setBusy_(txn_id, null);
  };

  const bulkApprove = async () => {
    const ids = selected.size > 0 ? [...selected] : filtered.map(t => t.txn_id);
    // One call each: approving is a decision per transaction, and the decision
    // log records who confirmed what. A bulk endpoint would flatten that.
    for (const id of ids) {
      try { await api.post(`/api/categorization/transactions/${id}/approve`); }
      catch (e) { console.error(e); }
    }
    load();
    setSelected(new Set());
  };

  const filtered = txns.filter(t => {
    if (statusFilter === 'needs_review') return !t.account_id;
    if (statusFilter === 'auto_cat') return !!t.account_id;
    return true;
  });

  const autoApprovedCount = txns.filter(t => t.status === 'auto_approved' || (t.account_id && t.confidence >= 0.85)).length;

  if (loading) return <div className="py-12 text-center text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="pending-transactions-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Pending Review</h1>
          <p className="text-sm text-slate-500 mt-0.5">{entity} — {txns.length} transaction{txns.length !== 1 ? 's' : ''} awaiting review</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* Cascade notification */}
      {cascadeMsg && (
        <div className="mb-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800" data-testid="cascade-msg">
          <Zap size={14} className="text-emerald-600 flex-shrink-0" />
          {cascadeMsg}
        </div>
      )}

      {/* Bulk action bar */}
      {txns.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Brain size={14} className="text-indigo-500" />
            <span><strong>{autoApprovedCount}</strong> auto-categorized by AI or vendor rules</span>
          </div>
          {autoApprovedCount > 0 && (
            <button onClick={bulkApprove}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 text-white text-xs font-semibold rounded-lg hover:bg-emerald-800"
              data-testid="bulk-approve-btn">
              <CheckCheck size={13} /> Approve All Auto-Categorized ({autoApprovedCount})
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-500">Show:</span>
            {['all', 'needs_review', 'auto_cat'].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${statusFilter === f ? 'bg-indigo-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {f === 'all' ? 'All' : f === 'needs_review' ? 'Needs Category' : 'AI Categorized'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Transactions table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
          <CheckCircle size={36} className="text-emerald-400 mx-auto mb-3" />
          <p className="font-semibold text-slate-700">All clear!</p>
          <p className="text-sm text-slate-400 mt-1">No transactions pending review.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-semibold text-slate-500 uppercase tracking-wide text-[10px] w-28">Date</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500 uppercase tracking-wide text-[10px]">Description</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-500 uppercase tracking-wide text-[10px] w-28">Amount</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500 uppercase tracking-wide text-[10px] w-52">Category</th>
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(txn => (
                <TxnRow
                  key={txn.txn_id}
                  txn={txn}
                  accounts={accounts}
                  busy={busy[txn.txn_id]}
                  selected={selected.has(txn.txn_id)}
                  onSelect={(v) => setSelected(p => { const s = new Set(p); v ? s.add(txn.txn_id) : s.delete(txn.txn_id); return s; })}
                  onCategorize={(aid, aname) => categorize(txn.txn_id, aid, aname)}
                  onApprove={() => approve(txn.txn_id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Vendor Rules panel */}
    </div>
  );
}

function TxnRow({ txn, accounts, busy, selected, onSelect, onCategorize, onApprove }) {
  const [localAccountId, setLocalAccountId] = useState(txn.account_id || '');
  const [localAccountName, setLocalAccountName] = useState(txn.account_name || '');

  const handleSelect = (e) => {
    const acc = accounts.find(a => a.account_id === e.target.value);
    setLocalAccountId(e.target.value);
    setLocalAccountName(acc?.account_name || '');
  };

  const hasCategory = !!localAccountId;
  const isAutoApproved = txn.status === 'auto_approved';
  const isVendorRule = txn.confidence >= 1 && !txn.ai_used;

  return (
    <tr className={`border-b border-slate-50 hover:bg-slate-50/60 ${selected ? 'bg-indigo-50/40' : ''}`} data-testid={`txn-row-${txn.txn_id}`}>
      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{txn.date}</td>
      <td className="px-4 py-3">
        <p className="text-slate-800 font-medium leading-snug">{txn.description}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          {isVendorRule && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded">Vendor Rule</span>}
          {txn.ai_used && txn.account_id && <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-semibold rounded flex items-center gap-0.5"><Brain size={9} /> AI</span>}
          {txn.ai_used && txn.account_id && <ConfidenceDot v={txn.confidence} />}
          {!txn.account_id && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded flex items-center gap-0.5"><AlertCircle size={9} /> Needs Category</span>}
        </div>
      </td>
      <td className="px-4 py-3 text-right font-mono font-semibold">{fmtAmt(txn.amount)}</td>
      <td className="px-4 py-3">
        <select value={localAccountId} onChange={handleSelect}
          className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
          data-testid={`cat-select-${txn.txn_id}`}>
          <option value="">— Select category —</option>
          {accounts.map(a => (
            <option key={a.account_id} value={a.account_id}>{a.account_name}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          {hasCategory && !isAutoApproved && (
            <button onClick={() => onCategorize(localAccountId, localAccountName)}
              disabled={!!busy}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-indigo-900 text-white text-[11px] font-semibold rounded hover:bg-indigo-800 disabled:opacity-50"
              data-testid={`save-cat-${txn.txn_id}`} title="Save & learn vendor rule">
              <Zap size={10} /> Save
            </button>
          )}
          {(isAutoApproved || (hasCategory && txn.confidence >= 0.85)) && (
            <button onClick={onApprove}
              disabled={!!busy}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-700 text-white text-[11px] font-semibold rounded hover:bg-emerald-800 disabled:opacity-50"
              data-testid={`approve-${txn.txn_id}`}>
              <CheckCircle size={10} /> Approve
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
